/**
 * HelpDrawerBody — corps scrollable du Help Drawer (extrait de HelpDrawer pour
 * tenir < 300 lignes). Aiguille entre : fil IA / chargement / erreur / lecture
 * d'article / vue liste.
 *
 * §3 : en lecture d'article, le **même champ unifié** (variant `article`) coiffe
 * l'article — porte de sortie globale + entrée IA — au lieu de l'ancienne barre
 * « IA pure ».
 */

import { ScrollArea } from '../ui/scroll-area'
import { type HelpArticle } from '../../hooks/useHelp'
import type { UseHelpAssistantReturn } from '../../hooks/useHelpAssistant'
import { ArticleView, LoadingState, ErrorState } from './HelpDrawerComponents'
import { AssistantConversation } from './HelpAssistant'
import { HelpSearchView, type HelpSearchViewProps } from './HelpSearchView'

export function HelpDrawerBody({
    inAssistantThread,
    assistant,
    onAsk,
    onReset,
    onSelectArticle,
    isLoading,
    error,
    currentArticle,
    pendingSection,
    searchProps,
}: {
    inAssistantThread: boolean
    assistant: UseHelpAssistantReturn
    onAsk: (value: string) => void
    onReset: () => void
    onSelectArticle: (slug: string, section?: string | null) => void
    isLoading: boolean
    error: string | null
    currentArticle: HelpArticle | null
    pendingSection: string | null
    /** Props du champ unifié, partagées entre la vue liste et la tête d'article. */
    searchProps: Omit<HelpSearchViewProps, 'variant'>
}) {
    return (
        <ScrollArea className="flex-1" viewportClassName="[&>div]:!block [&>div]:!min-w-0">
            <div className="w-full min-w-0 p-4">
                {inAssistantThread ? (
                    <AssistantConversation
                        messages={assistant.messages}
                        isStreaming={assistant.isStreaming}
                        error={assistant.error}
                        atLimit={assistant.atLimit}
                        onAsk={onAsk}
                        onReset={onReset}
                        onSelectArticle={onSelectArticle}
                    />
                ) : isLoading ? (
                    <LoadingState />
                ) : error ? (
                    <ErrorState message={error} />
                ) : currentArticle ? (
                    <>
                        <div className="mb-4">
                            <HelpSearchView variant="article" {...searchProps} />
                        </div>
                        <div className="min-w-0 overflow-x-auto rounded-xl bg-card p-5 shadow-md">
                            <ArticleView article={currentArticle} scrollToSection={pendingSection} />
                        </div>
                    </>
                ) : (
                    <HelpSearchView variant="list" {...searchProps} />
                )}
            </div>
        </ScrollArea>
    )
}
